import { useRuntimeConfig } from '#imports';
import { useSchemaOrgHead } from '~/composables/useSchemaOrgHead';
import type { ClientInitResponse } from '@/types/client/init';
import {
  clinicId,
  getOffices,
  normalizeSiteUrl,
  organizationId,
  parseOpeningHours,
  schemaTelephone,
  websiteId,
  type SchemaOrgNode,
} from '~/utils/schemaOrg';

export const useSchemaOrgSite = (init?: ClientInitResponse | null) => {
  if (!init) {
    return;
  }

  const siteUrl = normalizeSiteUrl(useRuntimeConfig().public.siteUrl as string);
  const offices = getOffices(init.affiliates);
  const email = (init.config?.headerEmail?.value as string) || '';
  const name = (init.config?.siteName?.value as string) || 'АО Стом';
  const logo = `${siteUrl}/android-chrome-192x192.png`;
  const orgId = organizationId(siteUrl);
  const phones = offices.map(office => schemaTelephone(office.phone)).filter(Boolean);

  const clinics: SchemaOrgNode[] = offices.map((office, index) => {
    const hours = parseOpeningHours(office.working_time);
    const node: SchemaOrgNode = {
      '@id': clinicId(siteUrl, index),
      '@type': ['MedicalClinic', 'Dentist'],
      name: office.title,
      url: `${siteUrl}/contacts/`,
      image: office.image?.source ? `${siteUrl}${office.image.source}` : logo,
      telephone: schemaTelephone(office.phone),
      medicalSpecialty: 'https://schema.org/Dentistry',
      parentOrganization: { '@id': orgId },
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'RU',
        addressLocality: 'Москва',
        streetAddress: office.address,
      },
    };

    if (email) {
      node.email = email;
    }

    if (office.latitude != null && office.longitude != null) {
      node.geo = {
        '@type': 'GeoCoordinates',
        latitude: office.latitude,
        longitude: office.longitude,
      };
    }

    if (hours) {
      node.openingHoursSpecification = [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: hours.opens,
          closes: hours.closes,
        },
      ];
    }

    return node;
  });

  const organization: SchemaOrgNode = {
    '@id': orgId,
    '@type': 'MedicalOrganization',
    name,
    url: `${siteUrl}/`,
    logo,
    medicalSpecialty: 'https://schema.org/Dentistry',
    department: clinics.map(clinic => ({ '@id': clinic['@id'] })),
  };

  if (email) {
    organization.email = email;
  }

  if (phones.length) {
    organization.telephone = phones.length === 1 ? phones[0] : phones;
  }

  const website: SchemaOrgNode = {
    '@id': websiteId(siteUrl),
    '@type': 'WebSite',
    url: `${siteUrl}/`,
    name,
    inLanguage: 'ru-RU',
    publisher: { '@id': orgId },
  };

  useSchemaOrgHead([website, organization, ...clinics], 'schema-org-site');
};
